import type { FC } from 'react';
import React, { useCallback, useContext, Fragment, useState, useEffect, useRef } from 'react';
import { cssMap, type CSSProperties, styled } from '@compiled/react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import isEqual from 'lodash/isEqual';
import memoize from 'memoize-one';

import { PROVIDER_ERROR_CODE } from '@atlaskit/editor-common/collab';
import type { EditorActions } from '@atlaskit/editor-core/actions';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { UFOThirdPartySegment } from '@atlaskit/react-ufo/third-party-segment';

import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { MIGRATE_PAGE_PUBLISH_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { BannerContext } from '@confluence/banners/entry-points/BannerContext.ts';
import { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { RestrictedPage } from '@confluence/no-permission/entry-points/RestrictedPage.ts';
import { useIsTransition } from '@confluence/route-manager/entry-points/RouteState.ts';
import { getParentContentId } from '@confluence/editor-flags/entry-points/SuccessFlag.ts';
import { ALLOWED_FLAG_MESSAGES } from '@confluence/editor-flags/entry-points/allowedFlagConstants.ts';
import {
	NativeCollabErrorFlags,
	NATIVE_COLLAB_ERROR_REASONS,
} from '@confluence/editor-flags/entry-points/NativeCollabFlags.ts';
import { EditorFlags } from '@confluence/editor-flags/entry-points/EditorFlags.ts';
import { NotFound } from '@confluence/not-found/entry-points/NotFound.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { isExpectedApolloError } from '@confluence/error-boundary/entry-points/expectedGraphQlErrorHelper.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { AccessLinkHeaderReplacementsContext } from '@confluence/external-share-context/entry-points/AccessLinkHeaderReplacements.ts';
import { WebPanelLocation } from '@confluence/web-panel-location/entry-points/WebPanelLocation.ts';
import {
	ATL_FOOTER,
	ATL_EDITOR_SAVEBAR,
	ATL_GENERAL,
} from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { useAutoConvertState } from '@confluence/editor-conversion/entry-points/useAutoConvertState.ts';
import { EditorConversionModal } from '@confluence/editor-conversion/entry-points/EditorConversionModal.ts';
import { CollabRecoveryErrorType } from '@confluence/native-collab/entry-points/useError.ts';
import { isHeartbeatPermissionError } from '@confluence/native-collab/entry-points/useEditorHeartbeat.ts';
import type { CollabHeartbeatError } from '@confluence/native-collab/entry-points/useEditorHeartbeat.ts';
import type { CollabEditState } from '@confluence/native-collab/entry-points/commonCollabTypes.ts';
import type { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import {
	PageTitleQuery,
	type PageTitleQuery$data,
	type PageTitleQuery$variables,
} from '@confluence/content-title/entry-points/PageTitleQuery.graphql.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getAMNReferralSourceFromQueryParam } from '@confluence/automated-meeting-notes/entry-points/getAMNReferralSourceFromQueryParam.ts';
import { useAutomatedMeetingNotesPageProperties } from '@confluence/automated-meeting-notes/entry-points/useAutomatedMeetingNotesPageProperties.ts';
import { ssrPermissionsErrorHandler } from '@confluence/ssr-utilities/entry-points/SSRPermissionsErrorHandler.ts';

import { useMigratedRecently } from '../hooks/useMigratedRecently/index.ts';
import { useSpaceKeyWatcher } from '../hooks/useSpaceKeyWatcher/index.ts';
import { useGeneralWebPanelHeight } from '../hooks/useGeneralWebPanelHeight.ts';
import type { DraftSyncResponseError } from '../hooks/useDraftSync/useDraftSync.tsx';

import { MigrationSuccessFlag } from './MigrationSuccessFlag.tsx';
import { SameFileNameWarningFlag } from './SameFileNameWarningFlag/SameFileNameWarningFlag.tsx';

export type EditorWrapperProps = {
	attribution: Attribution;
	contentId: string;
	contentType: string;
	isUnpublishedDraft: boolean;
	collabEditState: CollabEditState;
	forceDisableEditor: (forceDisable: boolean) => void;
	editorActions?: EditorActions; // used by SameFileNameWarningFlag
	heartbeatError?: CollabHeartbeatError;
	saveDraftError?: DraftSyncResponseError | null;
	stopHeartbeatCallback: () => void;
	children?: React.ReactNode;
	isLivePage?: boolean;
	editorDisabled?: boolean;
};

const HEARTBEAT_ERROR_FLAGS = ['tenant-unavailable', 'unauthorised', 'UNAUTHORIZED'];

const EditorSuccessFlags = withFlags(EditorFlags);

const styles = cssMap({
	wrapper: {
		height: 'calc(100vh - var(--ak-editor-wrapper-banner-height, 0px))',
		width: '100%',
	},
	withPanels: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		position: 'relative',
	},
});

// Note: this component extracts the props that we don't want to send to the DOM
// This avoids styled errors about these props being passed to the DOM
/* eslint-disable @typescript-eslint/no-unused-vars */
const PanelWrapper = ({
	showPanel,
	children,
	...props
}: {
	showPanel?: boolean;
	children: React.ReactNode;
	[key: string]: any;
}) => {
	return <div {...props}>{children}</div>;
};
/* eslint-enable @typescript-eslint/no-unused-vars */

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Panel = styled(PanelWrapper)<{
	showPanel?: boolean;
}>({
	margin: 0,
	flexShrink: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	display: (props) => (props?.showPanel ? 'flex' : 'none'),
	flexDirection: 'column',
});

const getFlagData = memoize((flagData) => flagData, isEqual);

export const EditorWrapper: FC<EditorWrapperProps> = ({
	attribution,
	forceDisableEditor,
	collabEditState,
	contentType,
	contentId,
	children,
	isUnpublishedDraft,
	editorActions,
	heartbeatError,
	stopHeartbeatCallback,
	saveDraftError,
	isLivePage,
	editorDisabled,
}) => {
	const { includeTopNav } = useContext(BannerContext);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { collabEditInitialized, collabEditError, collabRecoveryError } = collabEditState;
	const [showCreateFromExtensionFlag, setShowCreateFromExtensionFlag] = useState<{
		showFlag: boolean;
		flagData: any;
	}>({ showFlag: false, flagData: undefined });
	const [showRestoreSuccessFlag, setShowRestoreSuccessFlag] = useState<any>(false);
	const [blogifyConversionSuccess, setBlogifyConversionSuccess] = useState<{
		showFlag: boolean;
		flagData: any;
	}>({ showFlag: false, flagData: undefined });
	const [heartbeatErrorFlag, setHeartbeatErrorFlag] = useState<{
		showFlag: boolean;
		heartbeatError: CollabHeartbeatError | undefined;
	}>({ showFlag: false, heartbeatError: undefined });
	const [isAutoConverted] = useAutoConvertState();
	const { userId } = useSessionData();
	const isTransition = useIsTransition();
	const previousHasSaveDraftError = useRef<boolean>(false);
	const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();

	// Analytics
	const migratedRecently = useMigratedRecently(contentId);

	// track publish experience in page migration
	// copied from ContentEditorBindingsComponent
	if (migratedRecently) {
		experienceTracker.start({
			name: MIGRATE_PAGE_PUBLISH_EXPERIENCE,
			id: contentId,
			timeout: ExperienceTimeout.EDITOR_PUBLISH,
		});
	}

	// Show a draft create success flag if this draft was created from the create page extension
	useEffect(() => {
		// Ensure editor is ready
		if (collabEditInitialized && !editorDisabled) {
			const parentContentId = getParentContentId();
			if (parentContentId) {
				// Get the parent page title & show a draft create success flag
				let flagTimeout;
				queryInterop<PageTitleQuery$data, PageTitleQuery$variables>({
					query: PageTitleQuery,
					variables: { contentId: parentContentId },
				})
					.then((result) => {
						const parentTitle = result.data?.singleContent?.title;
						flagTimeout = setTimeout(() => {
							setShowCreateFromExtensionFlag({
								showFlag: true,
								flagData: {
									parentTitle,
									parentContentId,
								},
							});
						}, 1000);
					})
					.catch((e) => {
						if (isUnauthorizedError(e) || isExpectedApolloError(e)) {
							markErrorAsHandled(e);
						}
					});

				return () => {
					clearTimeout(flagTimeout);
				};
			}
		}
	}, [collabEditInitialized, editorDisabled]);

	const showBlogifyConversionSuccessFlag = blogifyConversionSuccess.showFlag;
	const blogifyConversionSuccessFlagData = blogifyConversionSuccess.flagData;

	const handleRestore = useCallback(
		(_delta: string, response: any) => {
			const { trigger } = response?.pageUpdated || {};
			const { accountId } = response || {};
			if (trigger === 'restore') {
				// Show this flag only if the same user triggered the restore
				// Otherwise, useUpdateEvent handles when a different user restores
				return !showRestoreSuccessFlag && userId === accountId && setShowRestoreSuccessFlag(true);
			}
			return false;
		},
		[showRestoreSuccessFlag, userId],
	);

	const onBlogifySubscriptionEvent = useCallback(
		(_eventName: string, response: any) => {
			// force disable NCS editor whenever page is converted to blogpost
			forceDisableEditor && forceDisableEditor(true);

			!showBlogifyConversionSuccessFlag &&
				response?.pageBlogified &&
				setBlogifyConversionSuccess({ showFlag: true, flagData: response?.pageBlogified });
		},
		[forceDisableEditor, showBlogifyConversionSuccessFlag],
	);

	/**
	 * To ensure that the editor is the correct height and doesn't have double scrollbars,
	 */
	const panelHeight = useGeneralWebPanelHeight();

	/**
	 * The access-link notice sits above the editor rather than in the page-top banner slot, so its
	 * height is not in `bannerState` and has to be subtracted separately.
	 */
	const { bannerHeight: accessLinkBannerHeight = 0 } = useContext(
		AccessLinkHeaderReplacementsContext,
	);

	useEffect(() => {
		if (heartbeatError) {
			if (heartbeatError.reason === 'tenant-unavailable') {
				forceDisableEditor(true);
			} else if (isHeartbeatPermissionError(heartbeatError)) {
				stopHeartbeatCallback();
			}

			if (heartbeatErrorFlag.heartbeatError?.reason !== heartbeatError.reason) {
				// Only update error flag data if error is different
				setHeartbeatErrorFlag({
					showFlag: HEARTBEAT_ERROR_FLAGS.includes(heartbeatError.reason),
					heartbeatError,
				});
			}
		}
		// exclude heartbeatErrorFlag.heartbeatError?.reason from dep list to prevent firing twice in a row
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [heartbeatError, forceDisableEditor, stopHeartbeatCallback]);

	const { isAMN, isRecurringParent } = useAutomatedMeetingNotesPageProperties(contentId);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (!isLivePage && isAMN) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'editAMNPage',
					attributes: {
						isRecurringParent,
						referralSource: getAMNReferralSourceFromQueryParam(),
					},
				},
			}).fire();
		}
	}, [contentId, isAMN, isRecurringParent, createAnalyticsEvent, isLivePage]);

	useSubscriptionEvent({
		contentId,
		contentType,
		subscriberId: 'EditorWrapperSubscriptionPageRestore',
		onDataReceived: handleRestore,
		delta: 'pageUpdated',
	});

	// GraphQL subscription for page blogified event
	useSubscriptionEvent({
		contentId,
		contentType,
		subscriberId: 'EditorWrapperSubscription',
		delta: 'pageBlogified',
		skip:
			// only listen for blogify events on pages
			contentType === 'blogpost',
		onDataReceived: onBlogifySubscriptionEvent,
	});

	// Update the URL of the draft with new space key
	// 1. when another users move the page to different space
	// 2. when user returns to a bookmarked page which has since been moved
	useSpaceKeyWatcher({ contentType });

	useEffect(() => {
		// Disable editor when saveDraftError exists, re-enable when error is reset
		if (saveDraftError) {
			forceDisableEditor(true);
			previousHasSaveDraftError.current = true;
		}
		if (saveDraftError === null && previousHasSaveDraftError.current === true) {
			// saveDraftError gets set to null explicitely when reset
			forceDisableEditor(false);
			previousHasSaveDraftError.current = false;
		}
	}, [saveDraftError, forceDisableEditor]);

	if (collabRecoveryError) {
		// Collab Reset mutation can return 403 or 404 directly from Confluence upon editor loading (i.e. perms/page deleted hasn't changed during editing session, so no flag)
		if (
			collabRecoveryError instanceof CollabRecoveryErrorType &&
			collabRecoveryError.status === 403
		) {
			return <RestrictedPage accessType="edit" contentId={contentId} />;
		} else if (
			collabRecoveryError instanceof CollabRecoveryErrorType &&
			collabRecoveryError.status === 404
		) {
			return <NotFound />;
		}
	}

	const isResourceDeletedOrArchived =
		collabEditError &&
		'reason' in collabEditError &&
		collabEditError.reason === NATIVE_COLLAB_ERROR_REASONS.RESOURCE_DELETED;

	if (collabEditError?.status === 403) {
		// The collab service has emitted an error.
		// Cases for the 403:
		//  (1) on initial editor load when the collab provider is not initialized
		//  (2) user's permissions are revoked during the editing session.
		//  (3) page was deleted or archived: if this is the case, reason will be NATIVE_COLLAB_ERROR_REASONS.RESOURCE_DELETED
		// Most of the time when a 403 occurs, we want to kick the user out of the editor, and in the second case we want to show an error flag explaining that permissions have changed.
		// If we're on a live page and the page was deleted or archived, we don't want to show <NotFound /> nor kick the user out. Instead we expect the editor to be disabled and we'll show a flag. (This also helps resolve a race condition where <NotFound /> can be shown before the archiving user gets redirected.)
		if (!isLivePage || (isLivePage && !isResourceDeletedOrArchived)) {
			return (
				<Fragment>
					{collabEditInitialized && <NativeCollabErrorFlags collabEditError={collabEditError} />}
					{isResourceDeletedOrArchived ? (
						<NotFound />
					) : (
						<RestrictedPage accessType="edit" contentId={contentId} />
					)}
				</Fragment>
			);
		} else if (isLivePage && isResourceDeletedOrArchived) {
			// Stop the heartbeat callback to prevent it from kicking the user out of the editor. We want the user to stay in the editor with a flag visible, until they refresh (or if they're the archiver, are redirected).
			stopHeartbeatCallback();
		}
	}

	const checkPermissionsErrorHandler = ssrPermissionsErrorHandler.getRestrictedRoute() === 'edit';

	if (!isTransition && checkPermissionsErrorHandler) {
		return <RestrictedPage accessType="edit" contentId={contentId} />;
	}

	if (isHeartbeatPermissionError(heartbeatError)) {
		return (
			<Fragment>
				<NativeCollabErrorFlags heartbeatError={heartbeatError} />
				<RestrictedPage accessType="edit" contentId={contentId} />
			</Fragment>
		);
	}

	if (saveDraftError) {
		// For non 403 and non 404 saveDraft errors, we still want to render editor content, but in disabled state
		if (saveDraftError.statusCode === 403 || saveDraftError.statusCode === 404) {
			return (
				<Fragment>
					<NativeCollabErrorFlags saveDraftError={saveDraftError} />
					{saveDraftError.statusCode === 403 && (
						<RestrictedPage accessType="edit" contentId={contentId} />
					)}
					{saveDraftError.statusCode === 404 && <NotFound />}
				</Fragment>
			);
		}
	}

	return (
		// TODO: display ADFParseError if passed in as a prop (from content reconciliation)
		<ErrorBoundary modal attribution={attribution}>
			<Subscribe to={[BannerStateContainer]}>
				{(bannerState: BannerStateContainer) => (
					// The wiki-content class name is required to avoid breaking macros that rely on DOM selectors
					<div
						css={styles.wrapper}
						data-testid="editor-wrapper"
						// Required by Confluence Connect JSAPI utils.isFabricEditor() for macro browser behavior.
						data-fabric-mode={isUnpublishedDraft ? 'create' : 'edit'}
						data-content-subtype={isLivePage ? 'live' : undefined}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className="wiki-content"
						style={
							{
								// We want to set the banner height to 0 when in Embedded Confluence because banners are inserted in the
								// BannerContainer component, which is rendered in the PageLayout component, which is not rendered in Embedded Confluence.
								'--ak-editor-wrapper-banner-height': isEmbeddedConfluence
									? '0px'
									: `${bannerState.getTotalHeight(includeTopNav) + panelHeight + accessLinkBannerHeight}px`,
							} as CSSProperties
						}
					>
						<div css={styles.withPanels}>
							<UFOThirdPartySegment name="atl-general-web-panel">
								<Panel>
									<WebPanelLocation location={ATL_GENERAL} fetchPolicy="cache-first" />
								</Panel>
							</UFOThirdPartySegment>
							{children}
							{!isLivePage && (
								<Panel>
									<WebPanelLocation location={ATL_EDITOR_SAVEBAR} />
								</Panel>
							)}
						</div>
						{/* We do this because we want to hide the panel's UI in live pages but keep the content (vendors often use background scripts for this footer extension point) */}
						<UFOThirdPartySegment name="atl-footer-web-panel">
							<Panel showPanel={false} data-testid="atl-footer-wrapper">
								<WebPanelLocation location={ATL_FOOTER} />
							</Panel>
						</UFOThirdPartySegment>
						{heartbeatErrorFlag.showFlag && (
							<NativeCollabErrorFlags heartbeatError={heartbeatErrorFlag.heartbeatError} />
						)}
						{collabEditError &&
							(collabEditError.status === 500 ||
								collabEditError.code === PROVIDER_ERROR_CODE.LOCKED ||
								// Document blocked (blacklisted, e.g. kill switch): keep the editor mounted but
								// disabled and show the blocked warning flag. Inert unless the error is emitted,
								// which only happens when platform_editor_blocked_document_ux is on.
								collabEditError.code === PROVIDER_ERROR_CODE.DOCUMENT_BLOCKED) && ( // unhandled error, no recovery steps at this point
								<NativeCollabErrorFlags collabEditError={collabEditError} />
							)}
						{collabRecoveryError &&
							collabRecoveryError instanceof CollabRecoveryErrorType &&
							collabRecoveryError.status === 500 && (
								<NativeCollabErrorFlags collabRecoveryError={collabRecoveryError} />
							)}
						{isLivePage &&
							collabEditError?.status === 403 &&
							isResourceDeletedOrArchived && ( // If page is archived or deleted while on a live page, show a flag
								<NativeCollabErrorFlags collabEditError={collabEditError} />
							)}
						{showRestoreSuccessFlag && (
							<EditorSuccessFlags
								flagData={getFlagData({
									[ALLOWED_FLAG_MESSAGES.RESTORE_CONTENT_SUCCESS]: true,
								})}
								contentId={contentId}
								contentType={contentType}
								isUnpublishedDraft={isUnpublishedDraft}
							/>
						)}
						{showBlogifyConversionSuccessFlag && (
							<EditorSuccessFlags
								flagData={getFlagData({
									[ALLOWED_FLAG_MESSAGES.BLOGIFY_BLOGPOST_SUCCESS]: true,
									...blogifyConversionSuccessFlagData,
								})}
								contentId={contentId}
								contentType={contentType}
								isUnpublishedDraft={isUnpublishedDraft}
							/>
						)}
						{showCreateFromExtensionFlag.showFlag && (
							<EditorSuccessFlags
								flagData={getFlagData({
									[ALLOWED_FLAG_MESSAGES.CREATED_FROM_EXTENSION]: true,
									...showCreateFromExtensionFlag.flagData,
								})}
							/>
						)}
						<MigrationSuccessFlag contentId={contentId} />
						{editorActions ? (
							<SameFileNameWarningFlag editorActions={editorActions} contentId={contentId} />
						) : null}
						{isAutoConverted ? <EditorConversionModal /> : null}
						{saveDraftError && <NativeCollabErrorFlags saveDraftError={saveDraftError} />}
					</div>
				)}
			</Subscribe>
		</ErrorBoundary>
	);
};
